import {
  Logo,
  Modal,
} from 'anf-core-react';
import PropTypes from 'prop-types';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDebounce, useLocalStorage, useSearchParam } from 'react-use';
import StoreDetailsContext from '../../context/StoreDetailsContext';
import useFocus from '../../hooks/useFocus';
import useTranslatedText from '../../hooks/useTranslatedText';

import {
  AUTOSUGGEST_THRESHOLD,
  CLICKED_SUGGESTION_KEY,
  GENDER_FACETS,
  PLACEMENT_IDS,
  SEARCH_FLYOUT_DEBOUNCE,
} from '../../tools/constants';
import getGenderPref from '../../tools/getGenderPref';
import prepareSearchTerm from '../../tools/prepareSearchTerm';
import AutoSuggestResult from '../AutoSuggestResult/AutoSuggestResult';
import BreakpointProvider from '../BreakpointProvider';
import SearchDefaultView from '../SearchDefaultView';
import SearchInputField from '../SearchInputField/SearchInputField';
import usePersistedSearchTerm from '../SearchWrapper/hooks/usePersistedSearchTerm';
import {
  useGenderLessSearch,
  useSuggestions,
} from '../SearchWrapper/hooks/useQueries';
import style from '../SearchWrapper/searchWrapper.module.scss';
import GenderFacetInput from '../SearchWrapper/tools/GenderFacetInput';
import searchBoxStyle from './SearchFlyout.module.scss';

const SearchFlyout = ({
  handleModalClose = () => { },
  isSearchToasterOpen = false,
  searchUrl = '',
}) => {
  const betaFlagGroup = useSearchParam('group');

  const closeMenu = useTranslatedText('closeMenu', { fallback: 'Close' });

  const [showRecsContainers, setShowRecsContainers] = useState(false);

  const { store, brand } = useContext(StoreDetailsContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypedSearchTerm, setUserTypedSearchTerm] = useState('');
  const [listPosition, setListPosition] = useState(-1);
  const [departmentId, setDepartmentId] = useState('');
  const [departmentGender, setDepartmentGender] = useState('');
  const [suggestedProductId, setSuggestedProductId] = useState('');
  const [, setCompletionType] = useLocalStorage(CLICKED_SUGGESTION_KEY, []);
  const [genderPref, setGenderPref] = useLocalStorage('genderPref', [GENDER_FACETS?.[store]?.[brand]] || []);
  const currentForm = useRef();
  const searchInputFieldRef = useRef(null);
  const [isFocused, setFocus, setFocusReturn] = useFocus(searchInputFieldRef);

  const clearLabel = useTranslatedText('autoSuggestClearButtonLabel', { fallback: 'Clear' });
  const placeholder = useTranslatedText('autoSuggestDesktopPlaceholder', { fallback: 'Search' });

  const { getSuggestions, suggestions } = useSuggestions();
  const genderLessSearch = useGenderLessSearch();

  const {
    combinedSuggestions,
    products,
    productTypes,
  } = suggestions;
  usePersistedSearchTerm(setSearchTerm, getSuggestions, setListPosition);

  const suggestionsVisible = useMemo(() => searchTerm.trim().length >= AUTOSUGGEST_THRESHOLD,
    [searchTerm]);
  const displayedData = useMemo(() => (suggestionsVisible
    ? combinedSuggestions : []), [suggestionsVisible, combinedSuggestions]);

  const lastIndex = useMemo(() => displayedData?.length - 1, [displayedData]);

  useEffect(() => {
    setShowRecsContainers(!suggestionsVisible);
  }, [suggestionsVisible]);

  const clearButtonHandler = useCallback(() => {
    setSearchTerm('');
    setUserTypedSearchTerm('');
    setFocusReturn(true);
  }, [setFocusReturn]);

  const debouncedSearchCallback = useCallback(() => {
    if (searchTerm && searchTerm.length >= AUTOSUGGEST_THRESHOLD
      && !isProcessing && listPosition === -1) {
      getSuggestions({ variables: { searchTerm } });
    }
  }, [searchTerm, isProcessing, listPosition, getSuggestions]);

  const [, cancelSearchQuery] = useDebounce(debouncedSearchCallback, SEARCH_FLYOUT_DEBOUNCE,
    [debouncedSearchCallback]);

  const searchInputHandler = useCallback((e) => {
    setSearchTerm(e.currentTarget.value || '');
    setUserTypedSearchTerm(e.currentTarget.value || '');
    if (e.target.value.length >= AUTOSUGGEST_THRESHOLD) {
      setListPosition(-1);
    }
  }, []);

  const formSubmitHandler = useCallback(async () => {
    setShowRecsContainers(false);
    await setSearchTerm(prepareSearchTerm(searchInputFieldRef.current?.value || ''));
    setCompletionType(null);
    setIsProcessing(true);
  }, [setCompletionType]);

  const listItemSubmitHandler = useCallback(async (listItemData, completionType = null) => {
    const {
      suggestedTerm,
      departmentIdentifier = '',
      departmentName = '',
      value,
      productId = '',
    } = listItemData;
    const suggestedTermValue = suggestedTerm || value;
    setShowRecsContainers(false);
    setCompletionType(completionType);
    setIsProcessing(true);
    setDepartmentId(departmentIdentifier);
    setDepartmentGender(departmentName);
    setSuggestedProductId(productId);
    setGenderPref(getGenderPref(departmentName, genderPref));

    await setSearchTerm(suggestedTermValue);
    currentForm.current.submit();
  }, [genderPref, setCompletionType, setGenderPref]);

  const keyboardActions = useMemo(() => ({
    ArrowDown: () => {
      if (listPosition < lastIndex) {
        setListPosition((previousPosition) => previousPosition + 1);
        setSearchTerm(displayedData[listPosition + 1]?.name ?? '');
        setDepartmentId(displayedData[listPosition + 1]?.departmentIdentifier ?? '');
        setDepartmentGender(displayedData[listPosition + 1]?.departmentName ?? '');
      } else {
        setListPosition(-1);
        setSearchTerm(userTypedSearchTerm);
      }
    },
    ArrowUp: () => {
      const newPosition = listPosition === -1 ? lastIndex : listPosition - 1;
      setListPosition(newPosition);
      if (newPosition >= 0) {
        setSearchTerm(displayedData[newPosition]?.name ?? '');
        setDepartmentId(displayedData[newPosition]?.departmentIdentifier ?? '');
        setDepartmentGender(displayedData[newPosition]?.departmentName ?? '');
      }
      if (newPosition === -1) {
        setSearchTerm(userTypedSearchTerm);
      }
    },
    Escape: () => {
      cancelSearchQuery();
      setSearchTerm('');
      setUserTypedSearchTerm('');
      setListPosition(-1);
    },
    Enter: () => {
      if (listPosition >= 0) {
        listItemSubmitHandler(displayedData[listPosition], displayedData[listPosition]?.imageId ? 'products' : 'suggestions');
      }
    },
  }), [
    displayedData,
    listPosition,
    lastIndex,
    userTypedSearchTerm,
    cancelSearchQuery,
    listItemSubmitHandler,
  ]);

  const keyDownHandler = useCallback((e) => {
    const { key } = e;
    keyboardActions[key]?.();
  }, [keyboardActions]);

  const blurHandler = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setFocus(false);
    }
  }, [setFocus]);

  const focusHandler = useCallback((e) => {
    if (!e.target.classList.contains('clear-button') && !e.target.classList.contains('search-button')) {
      setFocus(true);
    }
  }, [setFocus]);

  return (
    <BreakpointProvider>
      <Modal
        closeButtonLabel={closeMenu?.value}
        id="exposed-search-toaster"
        isFullscreen
        isOpen={isSearchToasterOpen}
        onClose={handleModalClose}
        variant="headerless"
      >
        <>
          <div className={`scope-1892 ${style.logoWrapper}`}>
            <Logo kind={brand === 'hol' ? 'hco' : brand} />
          </div>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <form
            ref={currentForm}
            action={searchUrl}
            className={`${searchBoxStyle.mfeSearchForm} rs-nav__search rs-nav__search-wrapper scope-1892`}
            data-testid="search-input-wrapper"
            id="product-search-form"
            method="get"
            onBlur={blurHandler}
            onFocus={focusHandler}
            onKeyDown={keyDownHandler}
            onSubmit={formSubmitHandler}
            role="search"
          >
            <div className={searchBoxStyle.inputGridContainer}>
              {betaFlagGroup
                ? (
                  <input
                    id="search-beta-flag"
                    name="group"
                    type="hidden"
                    value={betaFlagGroup}
                  />
                ) : null}
              {departmentId ? (
                <input
                  id="flyout-deptartment-id"
                  name="departmentId"
                  type="hidden"
                  value={departmentId}
                />
              ) : null}
              {suggestedProductId ? (
                <input
                  id="product-id"
                  name="productId"
                  type="hidden"
                  value={suggestedProductId}
                />
              ) : null}
              {genderLessSearch ? (
                <GenderFacetInput
                  suggestedGender={departmentGender}
                  userPrefGender={genderPref}
                />
              ) : null}
              <SearchInputField
                ref={searchInputFieldRef}
                autoComplete="off"
                buttonLabelText={placeholder.value}
                clearButtonLabelText={clearLabel.value}
                clearButtonOnClick={clearButtonHandler}
                id="search-input-field"
                isClearButton={suggestionsVisible}
                labelText={placeholder.value}
                name="searchTerm"
                onChange={searchInputHandler}
                placeholder={placeholder.value}
                value={searchTerm}
              />

              {suggestionsVisible
                ? (
                  <AutoSuggestResult
                    isFormFocused={isFocused}
                    isProcessing={isProcessing}
                    listItemSubmitHandler={listItemSubmitHandler}
                    listPosition={listPosition}
                    productData={products}
                    productTypesData={productTypes}
                    searchTerm={searchTerm}
                  />
                )
                : null}
            </div>

            {!suggestionsVisible
              ? (
                <div className={searchBoxStyle.searchDefaultViewWrapperContainer}>
                  <SearchDefaultView
                    clickableSearchChoiceHandler={listItemSubmitHandler}
                    clientId="search-flyout-1"
                    isFormFocused={isFocused}
                    placementIds={`${PLACEMENT_IDS.mobile.searchFlyout}|${PLACEMENT_IDS.desktop.searchFlyout}`}
                    searchDefaultViewPlace="search"
                    searchLabel={placeholder.value}
                    showRecsContainers={showRecsContainers}
                  />
                </div>
              )
              : null}
          </form>
        </>
      </Modal>
    </BreakpointProvider>
  );
};

SearchFlyout.propTypes = {
  isSearchToasterOpen: PropTypes.bool,
  handleModalClose: PropTypes.func,
  searchUrl: PropTypes.string,
};

export default SearchFlyout;
